/**
 * Maps human-readable keys to Framer XLIFF translation unit IDs
 * or manual key names for the report page.
 *
 * Framer keys are 22-char alphanumeric IDs; manual keys are camelCase
 * strings added directly to messages/{locale}.json.
 */
export const reportKeys = {
  // Hero
  heroLabel: "heroLabel", // "Your health report"
  heading: "heading", // "Finally, a health report that makes sense"
  subtitle: "subtitle", // "Get access to a personal health vault..."
  contactUs: "contactUs", // "Contact us"

  // Dashboard mock (section 2)
  dashboardTitle: "dashboardTitle", // "Health report"
  dashboardLastUpdated: "dashboardLastUpdated", // "Last updated: March 2026"
  selectedReport: "selectedReport", // "Selected report"
  bioAgeLabel: "bioAgeLabel", // "Biological age"
  bioAgeValue: "bioAgeValue", // "32"
  bioAgeUnit: "bioAgeUnit", // "years"
  bioAgeDesc: "bioAgeDesc", // "Your biological age is 6 years younger..."
  cardioRiskLabel: "cardioRiskLabel", // "Cardiovascular risk"
  cardioRiskValue: "cardioRiskValue", // "1.6%"
  cardioRiskDesc: "cardioRiskDesc", // "Your 10-year cardiovascular risk..."
  biomarkersAnalyzedCount: "biomarkersAnalyzedCount", // "81"
  biomarkersAnalyzedLabel: "biomarkersAnalyzedLabel", // "Biomarkers analyzed"
  biomarkersLabelShort: "biomarkersLabelShort", // "Biomarkers"
  findingsLabel: "findingsLabel", // "Findings"
  optimalLower: "optimalLower", // "optimal"
  inRangeLower: "inRangeLower", // "in range"
  outOfRangeLower: "outOfRangeLower", // "out of range"
  noFindingSingular: "noFindingSingular", // "No finding"
  minor: "minor", // "Minor"
  yrsLabel: "yrsLabel", // "yrs"
  observation: "observation", // "Observation"
  whatWorkingOnHeading: "whatWorkingOnHeading", // "What we're working on"
  workingOnLabReports: "workingOnLabReports", // "Add your past lab reports"
  workingOnWearable: "workingOnWearable", // "Connect your wearable device"
  workingOnDoctor: "workingOnDoctor", // "Share with your doctor"
  getDirections: "getDirections",
  addToCalendar: "addToCalendar",
  aheadAdvancedPackage: "aheadAdvancedPackage",
  threeAddOns: "threeAddOns",
  normalRangePill: "normalRangePill",
  radiologyFindingsCount: "radiologyFindingsCount", // "7"
  radiologyFindingsLabel: "radiologyFindingsLabel", // "Radiology findings"
  bodySystemsOverview: "bodySystemsOverview", // "Body systems overview"
  systemCardiovascular: "systemCardiovascular", // "Cardiovascular"
  systemMetabolic: "systemMetabolic", // "Metabolic"
  systemMusculoskeletal: "systemMusculoskeletal", // "Musculoskeletal"
  systemNeurological: "systemNeurological", // "Neurological"
  systemEndocrine: "systemEndocrine", // "Endocrine"
  statusNormal: "statusNormal", // "Normal"
  statusAttention: "statusAttention", // "Attention"

  // Section 2-3 heading
  sectionHeading: "sectionHeading", // "A report that doesn't feel like homework"
  sectionSubtitle: "sectionSubtitle", // "Because understanding your body shouldn't require a medical degree."
  bookCheckup: "bookCheckup", // "Book check-up"
  seeHealthData: "seeHealthData", // "See your health data in one place"
  everyDatapoint: "everyDatapoint", // "Every important datapoint, unified in one report."
  imagingMarkersLabel: "imagingMarkersLabel", // "Imaging markers"
  inRangeSuffix: "inRangeSuffix", // "/ {total} in range"
  findingsSuffix: "findingsSuffix", // "findings"

  // Feature cards (section 3)
  reportAtGlanceHeading: "reportAtGlanceHeading", // "Your report, at a glance"
  reportAtGlanceSubtitle: "reportAtGlanceSubtitle", // "Three core features..."
  personalizedScoresTitle: "personalizedScoresTitle", // "Personalized health scores"
  personalizedScoresDesc: "personalizedScoresDesc", // "Get a clearer read on your body's biological ageing."
  ageLabel56: "ageLabel56", // "56 years"
  ageLabel63: "ageLabel63", // "63 years"
  ageLabel67: "ageLabel67", // "67 years"
  clearActionItemsTitle: "clearActionItemsTitle", // "Clear action items"
  clearActionItemsDesc: "clearActionItemsDesc", // "Follow a clear plan prioritized by our leading doctors."
  actionOmega3: "actionOmega3", // "Increase omega-3 intake"
  actionFerritin: "actionFerritin", // "Schedule follow-up ferritin test"
  actionWalk: "actionWalk", // "30 min daily walk after meals"
  actionVitaminD: "actionVitaminD", // "Start Vitamin D supplementation"
  actionSugar: "actionSugar", // "Reduce refined sugar intake"
  dynamicTrackingTitle: "dynamicTrackingTitle", // "Dynamic journey tracking"
  dynamicTrackingDesc: "dynamicTrackingDesc", // "See how your body changes over time or reacts to changes."
  hba1cOverTime: "hba1cOverTime", // "HbA1c over time"
  diabetesRange: "diabetesRange", // "Diabetes (>6.5%)"
  preDiabetesRange: "preDiabetesRange", // "Pre-diabetes (5.7-6.4%)"
  normalRange: "normalRange", // "Normal (<5.7%)"

  // Section 4 — biomarker cards
  deepAnalysisLabel: "deepAnalysisLabel", // "Deep analysis"
  turnMarkersHeading: "turnMarkersHeading", // "Turn markers into meaning"
  turnMarkersSubtitle: "turnMarkersSubtitle", // "Our report explains key findings..."
  aboveRange: "aboveRange", // "Above range"
  normalRangeStatus: "normalRangeStatus", // "Normal range"

  // Section 5 — AI annotation + side-by-side
  radiologyLabel: "radiologyLabel", // "Radiology"
  aiAnnotationTitle: "aiAnnotationTitle", // "AI-based annotation of your body"
  aiAnnotationDesc: "aiAnnotationDesc", // "Easily understand highly technical scans."
  minorFinding: "minorFinding", // "1 minor finding"
  recommendedActions: "recommendedActions", // "2 recommended actions"
  imagingLabel: "imagingLabel", // "Imaging"
  sideBySideTitle: "sideBySideTitle", // "Side-by-side comparison"
  sideBySideDesc: "sideBySideDesc", // "Track internal changes over the years."

  // Section 6 heading
  tailoredPlanHeading: "tailoredPlanHeading", // "A plan tailored to your story"
  tailoredPlanSubtitle: "tailoredPlanSubtitle", // "No standard checklists. Our doctors shape each plan around your health, lifestyle, and goals."

  // Section 6 — recommendations
  recsHeading: "recsHeading", // "Recommendations that meet you where you are"
  recsSubtitle: "recsSubtitle", // "Every person gets different advice..."
  impactsBiomarkers: "impactsBiomarkers", // "Impacts {count} biomarkers"
  recTomTitle: "recTomTitle",
  recTomRole: "recTomRole",
  recTomCategory: "recTomCategory",
  recTomFrequency: "recTomFrequency",
  recDavidTitle: "recDavidTitle",
  recDavidRole: "recDavidRole",
  recDavidCategory: "recDavidCategory",
  recDavidFrequency: "recDavidFrequency",
  recClaudiaTitle: "recClaudiaTitle",
  recClaudiaRole: "recClaudiaRole",
  recClaudiaCategory: "recClaudiaCategory",
  recClaudiaFrequency: "recClaudiaFrequency",
  recEvaTitle: "recEvaTitle",
  recEvaRole: "recEvaRole",
  recEvaCategory: "recEvaCategory",
  recEvaFrequency: "recEvaFrequency",
  recLenaTitle: "recLenaTitle",
  recLenaRole: "recLenaRole",
  recLenaCategory: "recLenaCategory",
  recLenaFrequency: "recLenaFrequency",

  // Section 7 — bottom feature cards
  additionalInsightsTitle: "additionalInsightsTitle", // "Additional insights"
  additionalInsightsDesc: "additionalInsightsDesc", // "Background information on biomarkers..."
  chiefMedicalOfficer: "chiefMedicalOfficer", // "Chief medical officer"
  clearSignalsTitle: "clearSignalsTitle", // "Clear signals"
  clearSignalsDesc: "clearSignalsDesc", // "Confirm what's in great shape..."
  fastingGlucose: "fastingGlucose", // "Fasting glucose"
  withinNormalRange: "withinNormalRange", // "Within normal range"
  followUpTestsTitle: "followUpTestsTitle", // "Follow-up tests"
  followUpTestsDesc: "followUpTestsDesc", // "Easily book a follow-up..."
  intakeTitle: "intakeTitle", // "Your health history"
  intakeDesc: "intakeDesc", // "What you tell us before the scan shapes..."
  intakeItem1: "intakeItem1", // "Lifestyle & goals"
  intakeItem2: "intakeItem2", // "Family history"
  intakeItem3: "intakeItem3", // "Medications"
  intakeItem4: "intakeItem4", // "Symptoms"
  ferritinRetest: "ferritinRetest", // "Ferritin re-test"
  recommendedIn3Months: "recommendedIn3Months", // "Recommended in 3 months"
  scheduled: "scheduled", // "Scheduled"
  vitaminDCheck: "vitaminDCheck", // "Vitamin D check"
  followUpIn6Months: "followUpIn6Months", // "Follow-up in 6 months"
  confirmed: "confirmed", // "Confirmed"
  mockupApptDate: "mockupApptDate", // "October 25th" — appointment mockup date
  hba1cMonitoring: "hba1cMonitoring", // "HbA1c monitoring"
  quarterlyTracking: "quarterlyTracking", // "Quarterly tracking"
  bookNow: "bookNow", // "Book now"

  // Section 8 — trust icons
  madeForYouTitle: "madeForYouTitle", // "Made for you"
  madeForYouDesc: "madeForYouDesc", // "Each report is shaped by your life..."
  bankSecurityTitle: "bankSecurityTitle", // "Bank-level security"
  bankSecurityDesc: "bankSecurityDesc", // "Your medical data is 100% secure..."
  easySharingTitle: "easySharingTitle", // "Easy sharing"
  easySharingDesc: "easySharingDesc", // "Share your results with your doctor..."
  licensedPracticeTitle: "licensedPracticeTitle", // "Licensed Swiss practice"
  licensedPracticeDesc: "licensedPracticeDesc", // "We're fully Swiss licensed..."

  // Section 9 — testimonials
  whatOurClientsSay: "whatOurClientsSay", // "What our clients say"
  videoTitleMarco: "videoTitleMarco", // "My MRI experience at Ahead"
  videoSubtitleMarco: "videoSubtitleMarco", // "You can benefit from the preventive aspect..."
  videoTitleLucia: "videoTitleLucia", // "Preventive care that fits my schedule"
  videoSubtitleLucia: "videoSubtitleLucia", // "No more excuses. Even a packed schedule..."

  // Section 10 — FAQ
  faqHeading: "faqHeading", // "Frequently asked questions"

  // Section 11 — bottom CTA
  bottomCtaHeading: "bottomCtaHeading", // "See what's really going on inside your body"

  // Section 10 — doctor quote card
  doctorName: "doctorName", // "Dr. Olivio Donati"
  doctorQuote: "doctorQuote", // "Your hs-CRP is mildly elevated..."

  // Scattered data section (reuses some home namespace keys via manual keys)
  scatteredHeading: "scatteredHeading",
  scatteredSubtitle: "scatteredSubtitle",
  pillMedicalRecord: "report_pill_medical_record",
  pillMriScan: "report_pill_mri_scan",
  pillPrescriptions: "report_pill_prescriptions",
  pillBloodWork: "report_pill_blood_work",
  pillActionPlan: "report_pill_action_plan",
  pillLifestyleGoals: "report_pill_lifestyle_goals",
  scatteredCardName: "report_card_name",
  scatteredCardBiomarkers: "report_card_biomarkers",
  scatteredCardActionPoints: "report_card_action_points",

  // iPad report mockup (AHD-5388) — pixel-extracted recreation of my.aheadhealth.com,
  // not sourced from Framer XLIFF.
  mockupNavHome: "mockupNavHome",
  mockupNavHealthReport: "mockupNavHealthReport",
  mockupNavActionPlan: "mockupNavActionPlan",
  mockupMyAccount: "mockupMyAccount",
  mockupReportTitle: "mockupReportTitle",
  mockupReportVariant: "mockupReportVariant",
  mockupReportDate: "mockupReportDate",
  mockupSelectedReport: "mockupSelectedReport",
  mockupViewDetails: "mockupViewDetails",
  mockupDownloadReport: "mockupDownloadReport",
  mockupSidebarSummary: "mockupSidebarSummary",
  mockupSystemBrain: "mockupSystemBrain",
  mockupSystemCardiovascular: "mockupSystemCardiovascular",
  mockupSystemMusculoskeletal: "mockupSystemMusculoskeletal",
  mockupSystemLiver: "mockupSystemLiver",
  mockupSystemKidneys: "mockupSystemKidneys",
  mockupSystemVitamins: "mockupSystemVitamins",
  mockupSystemEndocrinology: "mockupSystemEndocrinology",
  mockupBioAge: "mockupBioAge",
  mockupBioAgeYearsShort: "mockupBioAgeYearsShort",
  mockupBioAgeDesc: "mockupBioAgeDesc",
  mockupHeartRisk: "mockupHeartRisk",
  mockupHeartRiskDesc: "mockupHeartRiskDesc",
  mockupBiomarkers: "mockupBiomarkers",
  mockupOptimal: "mockupOptimal",
  mockupInRange: "mockupInRange",
  mockupOutOfRange: "mockupOutOfRange",
  mockupFindings: "mockupFindings",
  mockupNoFinding: "mockupNoFinding",
  mockupObservation: "mockupObservation",
  mockupMinor: "mockupMinor",
  mockupSummary: "mockupSummary",
  mockupHello: "mockupHello",
  mockupSummaryText: "mockupSummaryText",
  mockupReadMore: "mockupReadMore",
  mockupClinicalFollowUp: "mockupClinicalFollowUp",
  mockupSpecialistConsult: "mockupSpecialistConsult",
  mockupHealthOptimizations: "mockupHealthOptimizations",
  mockupExerciseNutrition: "mockupExerciseNutrition",

  // AHD-5385 — hardcoded English strings in report-client.tsx that were
  // never wired to translations. Added for EN + translated for DE/FR/NL.

  // Section 3 — Clear action items card (purple todo list)
  todoListLabel: "todoListLabel",
  requiredTag: "requiredTag",
  todoPhysioTitle: "todoPhysioTitle",
  todoPhysioDesc: "todoPhysioDesc",
  todoBackTitle: "todoBackTitle",
  todoBackDesc: "todoBackDesc",
  todoWaterTitle: "todoWaterTitle",
  todoLabTitle: "todoLabTitle",
  // Section 3 — Action recommendation cards (anti-inflammatory diet, vitamin D)
  actionDietTitle: "actionDietTitle",
  actionDietDesc: "actionDietDesc",
  actionVitaminDTitle: "actionVitaminDTitle",
  actionVitaminDDesc: "actionVitaminDDesc",
  pillNutrition: "pillNutrition",
  pillSupplements: "pillSupplements",
  pillDaily: "pillDaily",
  pillThreeImpactAreas: "pillThreeImpactAreas",
  pillOneImpactArea: "pillOneImpactArea",
  threeRelevantBiomarkers: "threeRelevantBiomarkers",
  oneRelevantBiomarker: "oneRelevantBiomarker",

  // Section 3 — Dynamic tracking HbA1c chart
  preDiabetesBadge: "preDiabetesBadge",
  chartMonthSep: "chartMonthSep",
  hideDetails: "hideDetails",
  chartMonthApr: "chartMonthApr",
  chartMonthJun: "chartMonthJun",
  chartMonthAug: "chartMonthAug",
  chartMonthOct: "chartMonthOct",
  chartMonthDec: "chartMonthDec",
  chartMonthJan: "chartMonthJan",
  zoneDiabetes: "zoneDiabetes",
  zonePreDiabetes: "zonePreDiabetes",
  zoneNormal: "zoneNormal",
  resultHistoryLabel: "resultHistoryLabel",
  resultDateSep: "resultDateSep",
  resultDateJun: "resultDateJun",
  resultDateApr: "resultDateApr",
  statusIntermediate: "statusIntermediate",
  statusOutOfRange: "statusOutOfRange",
  historyPreDiabetes: "historyPreDiabetes",

  // Section 4 — Biomarker marquee ("See your health in color")
  marqueeHeading: "marqueeHeading",
  marqueeSubtitle: "marqueeSubtitle",
  bioCorticalGrayMatter: "bioCorticalGrayMatter",
  bioHbA1cNGSP: "bioHbA1cNGSP",
  bioFastingGlucose: "bioFastingGlucose",
  bioVisceralFat: "bioVisceralFat",
  bioIron: "bioIron",
  bioLDLCholesterol: "bioLDLCholesterol",
  bioVitaminD: "bioVitaminD",
  bioTSH: "bioTSH",
  statusAboveRange: "statusAboveRange",
  statusOptimal: "statusOptimal",

  // Section 7 — Doctor quote card + Clear signals
  doctorCardActions: "doctorCardActions",
  noActionsNeeded: "noActionsNeeded",
} as const
